import React from "react";
import ServicePageLayout, { H2, H3, P, UL } from "./ServicePageLayout";
import Certifications from "@/components/Certifications";

export const meta = {
  title: "Cellular DAS Alberta | Nextivity Certified Partner (CEL-FI / QUATRA EVO) | Epicom Systems",
  metaDescription:
    "Distributed Antenna System (DAS) design and commercial in-building cellular coverage across Southern Alberta. Nextivity Certified Partner (Professional level) with CEL-FI Enterprise Sales & Installation certification, covering CEL-FI, CEL-FI QUATRA and CEL-FI QUATRA EVO active DAS systems.",
  canonicalPath: "/cellular-das",
  jsonLd: {
    "@context": "https://schema.org",
    "@type": "Service",
    "@id": "https://epicom.ca/cellular-das#service",
    "name": "Cellular DAS Installation",
    "alternateName": "Distributed Antenna System Installation",
    "serviceType": "Distributed Antenna System (DAS) design and installation, including CEL-FI, CEL-FI QUATRA and CEL-FI QUATRA EVO active in-building cellular systems",
    "provider": { "@type": "LocalBusiness", "@id": "https://epicom.ca/#business" },
    "areaServed": { "@type": "AdministrativeArea", "name": "Southern Alberta" },
    "description":
      "Design and installation of Distributed Antenna Systems (DAS) and commercial in-building cellular coverage across Southern Alberta. Epicom Systems is a Nextivity Certified Partner (Professional level) with CEL-FI Enterprise Sales & Installation certification, working with Nextivity enterprise cellular solutions including CEL-FI, CEL-FI QUATRA and CEL-FI QUATRA EVO.",
    "url": "https://epicom.ca/cellular-das",
  },
};

export default function CellularDAS() {
  return (
    <ServicePageLayout
      title={meta.title}
      metaDescription={meta.metaDescription}
      canonicalPath={meta.canonicalPath}
      overline="Distributed Antenna Systems"
      h1="Cellular DAS & In-Building Cellular Coverage"
      intro="When staff, customers or equipment inside a commercial building can’t get a reliable cellular signal, the fix is rarely a plug-in booster from a big-box store. Epicom Systems designs and installs engineered Distributed Antenna Systems (DAS) and commercial cellular signal solutions that extend real carrier coverage — evenly, quietly and predictably — throughout Southern Alberta facilities."
      jsonLd={meta.jsonLd}
      relatedLinks={[
        { to: "/point-to-point-wireless", label: "Point-to-Point Wireless" },
        { to: "/unifi-installation", label: "UniFi Networking" },
        { to: "/business-wifi", label: "Business Wi-Fi" },
      ]}
    >
      <H2>What a Distributed Antenna System actually is</H2>
      <P>
        A Distributed Antenna System is exactly what it sounds like: instead of relying on
        one cellular antenna at the edge of a building (or nothing at all), a DAS distributes
        multiple small indoor antennas throughout the space, fed from a central donor signal.
        Users’ phones connect to whichever indoor antenna is closest, which means consistent
        bars, consistent throughput and clean handoffs as people move through the facility.
      </P>
      <P>
        A commercial DAS is not the same product category as a $300 residential cell booster.
        Consumer boosters are single-antenna, low-gain, single-user solutions designed for a
        room or two in a house. A commercial DAS is a coordinated indoor RF distribution
        network — engineered for the building, tuned for the carriers actually being used,
        and sized to handle simultaneous users across dozens or hundreds of connected devices.
      </P>

      <Certifications variant="full" className="!py-10 md:!py-14 !border-0" />

      <H2>Nextivity Certified Partner for CEL-FI Enterprise Systems</H2>
      <P>
        Epicom Systems is a <strong className="text-white/90">Nextivity Certified Partner</strong>{" "}
        participating in the Nextivity North America System Integrator Partner Program at
        the <strong className="text-white/90">Professional level</strong>. We have completed
        Nextivity's <strong className="text-white/90">CEL-FI Enterprise Sales and Installation
        Certification</strong> learning paths, giving us formal training on Nextivity's
        enterprise cellular coverage line — including CEL-FI, CEL-FI QUATRA and CEL-FI
        QUATRA EVO — for the commercial and institutional buildings we work with across
        Southern Alberta.
      </P>
      <P>
        As a Certified Partner we have access to the Nextivity Partner Portal for design
        and BOM tools, and to the Nextivity <strong className="text-white/90">WAVE platform</strong>{" "}
        for commissioning and monitoring installed CEL-FI systems. That means once a system
        is in the building, we can validate it against its design targets and provide
        ongoing visibility into how it's performing, rather than walking away at the panel
        lights.
      </P>

      <H3>CEL-FI, CEL-FI QUATRA and CEL-FI QUATRA EVO</H3>
      <P>
        CEL-FI is Nextivity's active in-building cellular product family. CEL-FI GO / SOLO
        units are appropriate for smaller commercial spaces — single-tenant offices,
        professional practices, and similar footprints. <strong className="text-white/90">
        CEL-FI QUATRA</strong> is Nextivity's active DAS platform for larger deployments
        where a distributed head-end and multiple network units make sense for the
        floorplate.
      </P>
      <P>
        <strong className="text-white/90">CEL-FI QUATRA EVO</strong> is Nextivity's
        scalable, all-digital active DAS hybrid for enterprise in-building cellular
        coverage. It can be configured for single- or dual-operator deployments and
        supports 3G, 4G LTE and 5G. QUATRA EVO uses category cabling and Power over
        Ethernet for distribution, with optional fiber expansion for larger coverage
        areas, and it can be remotely monitored and managed through the Nextivity{" "}
        <strong className="text-white/90">WAVE</strong> platform.
      </P>
      <P>
        Our Nextivity certification covers this enterprise ecosystem, so we can scope,
        design and install the right platform for the actual building — CEL-FI where
        CEL-FI fits, QUATRA or QUATRA EVO where the size and density of the space warrant
        an active DAS approach.
      </P>

      <H3>Multi-carrier active DAS and enterprise cellular coverage</H3>
      <P>
        For enterprise sites — larger offices, industrial facilities, institutional
        buildings — the right answer is usually an active DAS rather than a booster: an
        engineered network of remote units fed by a head-end, tuned to the carriers that
        matter to the tenant, and validated after commissioning. CEL-FI QUATRA and QUATRA
        EVO are the platforms we most commonly use for that class of project on Nextivity,
        and multi-carrier deployments are coordinated with the relevant Canadian carriers
        where the system architecture requires it.
      </P>

      <H3>Certification is training, not exclusivity</H3>
      <P>
        Partner status and CEL-FI certification mean Nextivity has trained us on how to
        design, install and validate their systems and given us the tools to do that
        properly. It does not mean we're the only people allowed to install these systems,
        and it does not mean we push them where they don't fit. Where a project genuinely
        needs a different platform, we say so.
      </P>

      <H2>Why commercial buildings lose cellular signal in the first place</H2>
      <P>
        Modern construction is the enemy of cellular RF. The same building materials that
        make facilities strong, energy-efficient and code-compliant also block the frequencies
        carriers use for LTE and 5G:
      </P>
      <UL>
        <>
          <strong className="text-white/90">Steel construction and metal roofs</strong> —
          the Faraday-cage effect. Reinforced steel and metal cladding reflect and absorb
          cellular signal on their way in.
        </>
        <>
          <strong className="text-white/90">Concrete, ICF and thick masonry</strong> —
          heavy attenuation, especially at higher frequency bands where signal loss is worst.
        </>
        <>
          <strong className="text-white/90">Low-E windows and metallic-film glazing</strong> —
          the same coatings that improve building energy performance also block cellular RF
          almost as effectively as a solid steel wall.
        </>
        <>
          <strong className="text-white/90">Building depth</strong> — signal that gets in
          through the exterior walls simply runs out before it reaches interior rooms, back
          offices, warehouses, mechanical spaces and basements.
        </>
        <>
          <strong className="text-white/90">Site geography</strong> — being in a valley, on
          the back side of a hill, or a long way from the nearest carrier tower means the
          exterior signal available to work with is already marginal.
        </>
      </UL>
      <P>
        The result is what most facility owners already know: no bars in the back office,
        dropped calls in the shop, texts that show up two hours late, and staff walking
        outside to make calls. On a busy commercial site, that’s a real productivity and
        safety problem — not a nice-to-have.
      </P>

      <H2>What a properly engineered DAS looks like</H2>
      <H3>Donor signal analysis</H3>
      <P>
        Every commercial cellular system starts with the donor signal — the outdoor signal
        the system is going to amplify and redistribute. We evaluate real-world signal levels
        for each supported carrier at the intended donor antenna location, look at line of
        sight to the nearest sector, and confirm there’s enough usable signal to build on.
        If the donor is weak, the interior distribution can only be so good, so this step
        drives everything downstream.
      </P>

      <H3>Indoor antenna distribution</H3>
      <P>
        Interior coverage is delivered through a network of low-power indoor antennas placed
        based on the actual floor plan — hallway ceilings, open office bays, back rooms,
        mechanical spaces, stairwells. Antennas are spaced so that overlapping coverage is
        deliberate, not accidental. That’s what makes calls and data hold together as
        somebody walks the building.
      </P>

      <H3>Multi-carrier considerations</H3>
      <P>
        In Canada, the practical question is which carriers your users are on — Rogers /
        Bell / Telus / their flankers. A well-designed system supports the carriers that
        actually matter for the site rather than assuming a single-carrier solution will
        cover everybody. We plan the RF path, filters and amplification to keep each supported
        band clean.
      </P>

      <H3>Testing and optimization</H3>
      <P>
        After installation, we walk-test the facility with real handsets and measurement
        gear, verify signal levels and quality at the points that matter (offices, service
        counters, back-of-house), and tune the system so it performs at spec — not just at
        the panel LEDs.
      </P>

      <H2>Where a DAS is the right answer</H2>
      <UL>
        <>Commercial and office buildings with poor indoor coverage in back offices or lower floors</>
        <>Industrial facilities, shops and manufacturing spaces where the building envelope kills signal</>
        <>Steel-clad ag buildings, feedlot offices and rural commercial buildings far from a tower</>
        <>Institutional facilities and community buildings where reliable in-building cellular matters for both staff and visitors</>
        <>Multi-tenant commercial buildings where each tenant expects coverage from their own carrier</>
        <>Large single-storey buildings where the interior is simply too deep for exterior signal to reach</>
      </UL>

      <H2>Plan cellular during construction, not after</H2>
      <P>
        The most cost-effective time to install cellular infrastructure is during
        construction, while ceilings are open and cable can be run cleanly through the same
        pathways as the rest of the low-voltage system. Retrofit installs are absolutely
        possible — most of what we do is retrofit — but if you’re planning a build, get us in
        at the design stage. We’ll coordinate donor antenna location, riser space and cable
        pathways with your GC, and the finished result is faster, cleaner and less expensive
        than trying to add it later.
      </P>

      <H2>Consumer booster or engineered DAS — how to tell</H2>
      <P>
        As a rough guide: if the space is a single room, a small residential footprint, or a
        vehicle, a consumer-grade cellular signal booster is often the right fit — see our{" "}
        <a href="/cell-boosters" className="text-signal hover:underline">Cell Boosters</a>{" "}
        service. If the space is a commercial or industrial facility, multiple floors, deep
        interior, steel construction, or has to support a whole team of users on multiple
        carriers, that’s DAS territory. The two systems solve related problems at very
        different scales, and picking the right one up front saves money and rework.
      </P>

      <H2>Working with Epicom on a cellular project</H2>
      <P>
        We start every commercial cellular project with a site conversation and a real
        signal evaluation — no guesses, no assumed coverage. From there we scope the system
        honestly, including whether a full DAS is even needed or whether a simpler solution
        will do the job. When we quote a DAS install, the design accounts for the building,
        the carriers your people actually use, and how the space is going to be used day to
        day.
      </P>
    </ServicePageLayout>
  );
}
